import React from 'react';
import styled from '@emotion/styled';
import { EmotionBreakpoint, LoadingIcon } from 'dw-us-ui';
import {
	CaseBuilderProvider,
	useCaseBuilderContext,
} from './context/CaseBuilderContext';
import SwapModal from './components/SwapModal/SwapModal';
import WineList from './components/WineList/WineList';
import MobileBottleCarousel from './components/MobileBottleCarousel/MobileBottleCarousel';
import CarouselDots from './components/CarouselDots/CarouselDots';
import WineDetails from './components/WineDetails/WineDetails';
import BannerHeader from './components/BannerHeader/BannerHeader';
import FreeGiftSection from './components/FreeGiftSection/FreeGiftSection';

const HeroWrapper = styled.div`
	background: #f8f5ef;
	padding: 48px 0;
	width: 100%;

	.law & {
		background: #f8f5ef;
	}
	.osw & {
		background: #f8f5ef;
	}
	.npr & {
		background: #ededed;
	}
	.fox & {
		background: #f8f8f8;
	}
	.wsj & {
		background: #f1f1f1;
	}

	${EmotionBreakpoint('mobile')} {
		padding: 24px 0;
	}
`;

const HeroContainer = styled.div`
	max-width: 1414px;
	margin: 0px auto;
	padding: 0 15px;
	${EmotionBreakpoint('mobile')} {
		padding: 0;
	}
`;

// Bottle Selection Styles
const BottleSelectionContainer = styled.div`
	background: #ffffff;
	border-radius: 8px;
	margin-bottom: 24px;

	${EmotionBreakpoint('mobile')} {
		display: flex;
		flex-direction: column;
	}
	${EmotionBreakpoint('tablet')} {
		display: flex;
		flex-direction: column;
	}
`;

const BottleSelectionWrapper = styled.div`
	padding: 24px 50px;

	${EmotionBreakpoint('mobile')} {
		padding: 24px 24px 0;
		order: 1;
		display: flex;
		flex-direction: column;
		gap: 24px;
	}
	${EmotionBreakpoint('tablet')} {
		padding: 24px;
		order: 1;
		display: flex;
		flex-direction: column;
		gap: 24px;
	}
`;

// Inner component that uses context
const CaseBuilderContent = () => {
	const {
		dataLoading,
		dataError,
		isReady,
		contentfulData,
		pricingSummary,
		showSwapModal,
		activeSwaps,
		currentCarouselSlide,
		closeModal,
		swapWines,
		previousSlide,
		nextSlide,
		goToSlide,
	} = useCaseBuilderContext();

	// Loading state
	if (dataLoading || !isReady) {
		return (
			<HeroWrapper>
				<HeroContainer>
					<div style={{ textAlign: 'center', padding: '60px 0' }}>
						<LoadingIcon />
					</div>
				</HeroContainer>
			</HeroWrapper>
		);
	}

	// Error state
	if (dataError) {
		return (
			<HeroWrapper>
				<HeroContainer>
					<div style={{ textAlign: 'center', padding: '60px 0' }}>
						<p style={{ color: '#d32f2f', fontSize: '18px' }}>
							Error loading case builder. Please try again.
						</p>
					</div>
				</HeroContainer>
			</HeroWrapper>
		);
	}

	return (
		<HeroWrapper>
			<HeroContainer>
				<BannerHeader
					mainTitle={contentfulData?.title}
					description={contentfulData?.description?.fields?.content}
					pricingSummary={pricingSummary}
				/>

				<BottleSelectionContainer>
					<BottleSelectionWrapper>
						{/* Desktop: Wine List */}
						<WineList />

						{/* Mobile: Bottle Carousel */}
						<MobileBottleCarousel />

						{/* Carousel Dots */}
						<CarouselDots />
					</BottleSelectionWrapper>

					{/* Wine Details */}
					<WineDetails />
				</BottleSelectionContainer>

				{/* Free Gift */}
				{contentfulData &&
					contentfulData?.freeGift &&
					contentfulData?.freeGiftContent?.fields?.content && (
						<FreeGiftSection
							freeGiftUrl={
								contentfulData?.freeGiftImage?.fields?.largeDeviceImage?.fields
									?.file?.url
							}
							freeGiftDescription={
								contentfulData?.freeGiftContent?.fields?.content
							}
						/>
					)}

				{/* Swap Modal */}
				<SwapModal
					showSwapModal={showSwapModal}
					closeSwapModal={closeModal}
					activeSwaps={activeSwaps}
					currentCarouselSlide={currentCarouselSlide}
					goToSlide={goToSlide}
					swapWines={swapWines}
					previousSlide={previousSlide}
					nextSlide={nextSlide}
				/>
			</HeroContainer>
		</HeroWrapper>
	);
};

// Main export wraps everything in Provider
const CaseBuilder = () => {
	return (
		<CaseBuilderProvider>
			<CaseBuilderContent />
		</CaseBuilderProvider>
	);
};

export default CaseBuilder;
